import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CheckIcon,
  Combobox,
  Group,
  InputBase,
  Text,
  useCombobox,
} from '@mantine/core'
import { DataTable, DataTableSortStatus } from 'mantine-datatable'
import {
  ColumnFilter,
  Filter,
  FilterDataType,
  DEFAULT_FILTER_BY_TYPE,
  applyColumnFilter,
  isFilterActive,
} from './ColumnFilter'
import { DownloadSimpleIcon } from '@phosphor-icons/react'
import { COLUMN_WIDTH_MIN, estimateColumnWidth } from '../utils/columnWidth'
import { loadFields, saveFields } from '../utils/fieldStorage'
import { TablePagination } from './TablePagination'

// テーブル表示時にnullと空文字はハイフンに直す
function displayCV(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.length > 0 ? value : '-'
  return String(value)
}

// 列ソート用比較関数
function compareCellValues(a: CvCell, b: CvCell): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'ja')
}

export type CvColumn = { headerName: string; field: string }
export type CvCell = string | number | null
export type CvRow = Record<string, CvCell>
export type CvListData = { columns: CvColumn[]; rows: CvRow[] }

// --- CSV出力 ---
function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function buildCsv(columns: CvColumn[], rows: CvRow[]): string {
  const header = columns.map((c) => escapeCsvValue(c.headerName)).join(',')
  const body = rows
    .map((row) => columns.map((c) => escapeCsvValue(row[c.field])).join(','))
    .join('\r\n')
  return `${header}\r\n${body}`
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function defaultCsvFileName(): string {
  const d = new Date()
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  return `cv_list_${ymd}.csv`
}
// --- CSV出力 ---

interface Props {
  data: CvListData
  csvFileName?: string
  storageKey?: string
  fetching?: boolean
  selectedRecords?: CvRow[]
  onSelectedRecordsChange?: (records: CvRow[]) => void
  isRecordSelectable?: (record: CvRow, index: number) => boolean
  idAccessor?: string | ((record: CvRow) => React.Key)
  /** 列フィルタ変更時に呼ばれる。絞り込みで非表示になった行の選択を呼び出し側が解除するために使う */
  onFiltersChange?: () => void
}

export const CVListTable: React.FC<Props> = ({
  data,
  csvFileName,
  storageKey,
  fetching,
  selectedRecords,
  onSelectedRecordsChange,
  isRecordSelectable,
  idAccessor,
  onFiltersChange,
}) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [visibleFields, setVisibleFields] = useState<string[]>(() =>
    loadFields(storageKey, data.columns.map((c) => c.field)),
  )
  const [columnSearch, setColumnSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, Filter>>({})
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CvRow> | null>(null)

  // 表示データ変更時初期化
  useEffect(() => {
    setPage(1)
    setVisibleFields(loadFields(storageKey, data.columns.map((c) => c.field)))
    setFilters({})
    setSortStatus(null)
  }, [data, storageKey])

  // カラム選択コンボボックスの開閉制御
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setColumnSearch('')
    },
  })

  // カラムの表示/非表示切替（ユーザー操作なのでここで localStorage 保存も行う）
  const toggleField = (field: string) => {
    setVisibleFields((current) => {
      const next = current.includes(field) ? current.filter((v) => v !== field) : [...current, field]
      saveFields(storageKey, next)
      return next
    })
  }

  // カラム名検索による候補絞り込み
  const filteredColumns = useMemo(() => {
    const q = columnSearch.trim().toLowerCase()
    if (!q) return data.columns
    return data.columns.filter(
      (c) => c.headerName.toLowerCase().includes(q) || c.field.toLowerCase().includes(q),
    )
  }, [data.columns, columnSearch])

  // 各カラムのデータ型推定（数値/文字列）
  const columnDataTypes = useMemo<Record<string, FilterDataType>>(() => {
    const result: Record<string, FilterDataType> = {}
    for (const col of data.columns) {
      const samples: unknown[] = []
      for (const row of data.rows) {
        if (samples.length >= 10) break
        const v = row[col.field]
        if (v === null || v === undefined || v === '') continue
        samples.push(v)
      }
      if (samples.length === 0) {
        result[col.field] = 'string'
        continue
      }
      const allNumeric = samples.every(
        (v) =>
          typeof v === 'number' ||
          (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))),
      )
      result[col.field] = allNumeric ? 'number' : 'string'
    }
    return result
  }, [data.rows, data.columns])

  // 各カラムの推定幅算出
  const columnWidths = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {}
    const sampleRows = data.rows.slice(0, 20)
    for (const col of data.columns) {
      const samples = sampleRows.map((r) => r[col.field])
      result[col.field] = estimateColumnWidth(col.headerName, samples)
    }
    return result
  }, [data.rows, data.columns])

  // フィルタ適用後の行
  const filteredRows = useMemo(() => {
    return data.rows.filter((row) =>
      Object.entries(filters).every(([field, f]) => {
        if (!isFilterActive(f)) return true
        return applyColumnFilter(row[field], f, columnDataTypes[field] ?? 'string')
      }),
    )
  }, [data.rows, filters, columnDataTypes])

  // ソート適用後の行
  const sortedRows = useMemo(() => {
    if (!sortStatus) return filteredRows
    const { columnAccessor, direction } = sortStatus
    const sign = direction === 'desc' ? -1 : 1
    return [...filteredRows].sort(
      (a, b) => sign * compareCellValues(a[columnAccessor], b[columnAccessor]),
    )
  }, [filteredRows, sortStatus])

  const total = sortedRows.length
  const pageCnt = Math.max(1, Math.ceil(total / perPage))

  // DataTable向けカラム定義生成
  const tableColumns = useMemo(
    () =>
      data.columns
        .filter((c) => visibleFields.includes(c.field))
        .map((col) => {
          const dataType = columnDataTypes[col.field] ?? 'string'
          const isNumber = dataType === 'number'
          return {
            accessor: col.field,
            title: col.headerName,
            sortable: true,
            resizable: true,
            ellipsis: true,
            width: columnWidths[col.field] ?? COLUMN_WIDTH_MIN,
            titleStyle: isNumber ? (): React.CSSProperties => ({ textAlign: 'right' }) : undefined,
            cellsStyle: isNumber ? (): React.CSSProperties => ({ textAlign: 'right' }) : undefined,
            render: (r: CvRow) => displayCV(r[col.field]),
            filter: (
              <ColumnFilter
                filter={filters[col.field] ?? DEFAULT_FILTER_BY_TYPE[dataType]}
                onChange={(f) => {
                  // 絞り込みで総ページ数が減ると現在ページが範囲外になり空表示になるため先頭に戻す
                  setPage(1)
                  setFilters((prev) => ({ ...prev, [col.field]: f }))
                  // 非表示になった行の選択が残ると意図しない行が対象に含まれるため解除させる
                  onFiltersChange?.()
                }}
                dataType={dataType}
              />
            ),
            filtering: isFilterActive(filters[col.field]),
          }
        }),
    [data.columns, visibleFields, filters, columnDataTypes, columnWidths, onFiltersChange],
  )

  // 現在ページ分の行
  const pagedRows = useMemo(() => {
    const from = (page - 1) * perPage
    return sortedRows.slice(from, from + perPage)
  }, [sortedRows, page, perPage])

  // 表示中カラム/絞り込み済みデータをCSVダウンロード
  const handleExportCsv = () => {
    const visibleColumns = data.columns.filter((c) => visibleFields.includes(c.field))
    const csv = buildCsv(visibleColumns, sortedRows)
    downloadCsv(csvFileName ?? defaultCsvFileName(), csv)
  }

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = pagedRows.length === 0 ? 400 : pagedRows.length >= 15 ? 600 : undefined

  return (
    <>
      <Box mt={40}>
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => toggleField(val)}
          position="bottom-start"
          withinPortal={false}
        >
          <Combobox.Target>
            <InputBase
              component="button"
              type="button"
              pointer
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
              onClick={() => combobox.toggleDropdown()}
              w={250}
            >
              {visibleFields.length > 0 ? (
                `${visibleFields.length}カラム選択中`
              ) : (
                <Text c="dimmed" fz="sm">
                  カラムを選択してください
                </Text>
              )}
            </InputBase>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Search
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.currentTarget.value)}
              placeholder="カラム名で検索"
            />
            <Combobox.Options mah={300} style={{ overflowY: 'auto' }}>
              {filteredColumns.length === 0 ? (
                <Combobox.Empty>該当するカラムがありません</Combobox.Empty>
              ) : (
                filteredColumns.map((col) => {
                  const checked = visibleFields.includes(col.field)
                  return (
                    <Combobox.Option value={col.field} key={col.field} active={checked}>
                      <Group gap="sm" wrap="nowrap">
                        {checked ? <CheckIcon size={12} /> : <Box w={12} />}
                        <span>{col.headerName}</span>
                      </Group>
                    </Combobox.Option>
                  )
                })
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </Box>

      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
        mt={16}
      >
        <DataTable
          withColumnBorders
          fetching={fetching}
          records={pagedRows}
          noRecordsText="データがありません"
          height={tableHeight}
          styles={{
            root: { border: 'none' },
            header: {
              backgroundColor: '#F8F9FA',
            },
          }}
          columns={tableColumns}
          sortStatus={sortStatus ?? { columnAccessor: '', direction: 'asc' }}
          onSortStatusChange={setSortStatus}
          selectedRecords={selectedRecords}
          onSelectedRecordsChange={onSelectedRecordsChange}
          isRecordSelectable={isRecordSelectable}
          idAccessor={idAccessor}
        />

        <TablePagination
          page={page}
          onChangePage={setPage}
          perPage={perPage}
          onChangePerPage={(v) => {setPerPage(v); setPage(1)}}
          totalPages={pageCnt}
          isEmpty={pagedRows.length === 0}
        />
      </Box>
      <Group mt={16} justify='end'>
        <Button
          variant="outline"
          radius="xl"
          onClick={handleExportCsv}
          leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
        >
          CSVダウンロード
        </Button>
      </Group>
    </>
  )
}
